import type { Component } from 'vue';
import type { RouteLocationRaw } from 'vue-router';

export interface NxRouteTab {
  /** Router target of the tab. */
  to: RouteLocationRaw;
  label: string;
  /** Stable key; the resolved path when omitted. */
  key?: string | number;
  icon?: Component;
  /** A count or short word after the label; hidden when `null`/`undefined`/`''`. */
  badge?: number | string | null;
  /** Spoken meaning of the badge („3 offen“); read instead of the bare figure. */
  badgeLabel?: string;
  /**
   * Active only when the current path equals the tab's path. Off by default:
   * the tab also lights up for the pages below it (`/drive` for
   * `/drive/mounts/7`), unless a sibling tab matches more of the path.
   */
  exact?: boolean;
  /** Route names that also make this tab the active one (a detail page elsewhere). */
  activeNames?: readonly string[];
  disabled?: boolean;
}

/** Strips a trailing slash, so `/drive/` and `/drive` compare equal. */
function normalize(path: string): string {
  return path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
}

/**
 * Index of the tab that owns `current`, or -1. One tab at most: an exact path
 * or a listed route name wins, otherwise the tab whose path covers the most of
 * the current path — so `/drive` and `/drive/shares` side by side never both
 * light up on `/drive/shares`.
 */
export function activeRouteTab(
  tabs: readonly { path: string; exact?: boolean; activeNames?: readonly string[] }[],
  current: { path: string; name?: string | symbol | null }
): number {
  const here = normalize(current.path);
  const name = typeof current.name === 'string' ? current.name : undefined;
  let best = -1;
  let bestLength = -1;
  for (let i = 0; i < tabs.length; i++) {
    const tab = tabs[i]!;
    const path = normalize(tab.path);
    if (path === here || (name !== undefined && tab.activeNames?.includes(name))) return i;
    if (tab.exact) continue;
    const covers = path === '/' || here.startsWith(`${path}/`);
    if (covers && path.length > bestLength) {
      best = i;
      bestLength = path.length;
    }
  }
  return best;
}
